import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowRight, Check, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { cn } from '@/lib/utils'
import { ClassesTab } from '@/components/tables/ClassesTab'
import { AttributesTab } from '@/components/tables/AttributesTab'
import { ItemsTab } from '@/components/tables/ItemsTab'
import type { User } from '@/types'

const STEPS = [
  { key: 'classes', label: 'Classes' },
  { key: 'attributes', label: 'Atributos' },
  { key: 'items', label: 'Itens' },
] as const

type SystemStep = (typeof STEPS)[number]['key']

// Progresso do stepper por mesa. O conteúdo (classes/atributos/itens) já é
// salvo no servidor a cada operação; aqui persiste só a maior etapa confirmada,
// para retomar do mesmo ponto se o mestre sair antes de concluir.
const stepStorageKey = (tableId: string) => `edurpg-setup-etapa-${tableId}`

function loadSavedStep(tableId: string): number {
  const raw = Number(localStorage.getItem(stepStorageKey(tableId)))
  return Number.isInteger(raw) && raw >= 0 && raw < STEPS.length ? raw : 0
}

interface ConfigureSystemScreenProps {
  user?: User
}

/**
 * Tela 2 do wizard de criação de campanha (rota /campanhas/:tableId/sistema).
 * O mestre revê o sistema de jogo em etapas sequenciais — confirma as classes,
 * depois os atributos, depois os itens — e só então "Concluir configuração"
 * grava system_setup_complete no servidor. Enquanto pendente, o /detalhes
 * redireciona de volta para cá (docs/PLANO_CRIACAO_CAMPANHA.md).
 * A etapa alcançada fica em localStorage; sair e voltar retoma do mesmo ponto.
 */
export function ConfigureSystemScreen(_props: ConfigureSystemScreenProps) {
  const { tableId } = useParams<{ tableId: string }>()
  const navigate = useNavigate()

  const [tableName, setTableName] = useState('')
  const [userRole, setUserRole] = useState<string | null>(null)
  const [gameConfig, setGameConfig] = useState<any>(null)
  const [stepIndex, setStepIndex] = useState(() => (tableId ? loadSavedStep(tableId) : 0))
  // Maior etapa já confirmada + 1 (etapas <= reached são navegáveis para trás)
  const [reachedIndex, setReachedIndex] = useState(stepIndex)
  const [isLoading, setIsLoading] = useState(true)
  const [isFinishing, setIsFinishing] = useState(false)

  const step: SystemStep = STEPS[stepIndex].key
  const isLastStep = stepIndex === STEPS.length - 1

  const reloadGameConfig = useCallback(async () => {
    if (!tableId) return
    try {
      const config = await apiService.getGameConfig(tableId)
      setGameConfig(config)
    } catch (error) {
      console.error('Erro ao carregar game_config:', error)
      toast.error('Não foi possível carregar o sistema de jogo.')
    }
  }, [tableId])

  useEffect(() => {
    if (!tableId) return
    let cancelled = false

    const load = async () => {
      try {
        const [details, config] = await Promise.all([
          apiService.getTableDetails(tableId),
          apiService.getGameConfig(tableId),
        ])
        if (cancelled) return
        setTableName(details.name)
        setUserRole(details.user_role ?? null)
        setGameConfig(config)
      } catch (error) {
        console.error('Erro ao carregar a campanha:', error)
        toast.error('Não foi possível carregar a campanha.')
        navigate('/pagina-inicial', { replace: true })
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [tableId, navigate])

  // Aluno/visitante não configura sistema: segue para a mesa
  useEffect(() => {
    if (!isLoading && userRole && userRole !== 'mestre') {
      navigate(`/detalhes/${tableId}`, { replace: true })
    }
  }, [isLoading, userRole, tableId, navigate])

  const handleSaveClass = async (classData: any) => {
    if (!tableId) return
    if (classData.id && gameConfig?.classes?.some((c: any) => c.id === classData.id)) {
      await apiService.updateGameConfigClass(tableId, classData.id, classData)
    } else {
      await apiService.createGameConfigClass(tableId, classData)
    }
  }

  const handleDeleteClass = async (classId: string) => {
    if (!tableId) return
    await apiService.deleteGameConfigClass(tableId, classId)
  }

  const handleSaveAttribute = async (attributeData: any) => {
    if (!tableId) return
    if (attributeData.id && gameConfig?.attributes?.some((a: any) => a.id === attributeData.id)) {
      await apiService.updateGameConfigAttribute(tableId, attributeData.id, attributeData)
    } else {
      await apiService.createGameConfigAttribute(tableId, attributeData)
    }
  }

  const handleDeleteAttribute = async (attributeId: string) => {
    if (!tableId) return
    await apiService.deleteGameConfigAttribute(tableId, attributeId)
  }

  const handleSaveItem = async (itemData: any) => {
    if (!tableId) return
    if (itemData.id && gameConfig?.items?.some((i: any) => i.id === itemData.id)) {
      await apiService.updateGameConfigItem(tableId, itemData.id, itemData)
    } else {
      await apiService.createGameConfigItem(tableId, itemData)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    if (!tableId) return
    await apiService.deleteGameConfigItem(tableId, itemId)
  }

  const handleConfirmStep = () => {
    const next = stepIndex + 1
    const reached = Math.max(reachedIndex, next)
    setReachedIndex(reached)
    if (tableId) localStorage.setItem(stepStorageKey(tableId), String(reached))
    setStepIndex(next)
  }

  const handleFinish = async () => {
    if (!tableId) return
    setIsFinishing(true)
    try {
      await apiService.completeTableSetup(tableId)
      localStorage.removeItem(stepStorageKey(tableId))
      toast.success('Campanha configurada! Agora é só convidar a turma.')
      navigate(`/detalhes/${tableId}`)
    } catch (error) {
      console.error('Erro ao concluir configuração:', error)
      toast.error('Não foi possível concluir. Tente novamente.')
      setIsFinishing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-3xl pb-28">
      {/* Cabeçalho: passo 2 do wizard, com a etapa atual */}
      <div className="pb-1 pt-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-primary">
          Passo 2 de 2 · Etapa {stepIndex + 1} de {STEPS.length} · {STEPS[stepIndex].label}
        </p>
        <h1 className="mt-1 text-2xl font-black italic tracking-tighter">{tableName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Confirme cada parte do sistema — classes, atributos e itens — para liberar a campanha.
          Tudo o que você criar ou editar fica salvo, mesmo se sair antes de concluir.
        </p>
      </div>

      {/* Stepper: etapas já alcançadas são clicáveis (voltar); futuras, bloqueadas */}
      <div className="mt-4 flex gap-1 rounded-lg bg-section p-1" role="tablist" aria-label="Sistema de jogo">
        {STEPS.map(({ key, label }, index) => {
          const isCurrent = index === stepIndex
          const isConfirmed = index < reachedIndex
          const isLocked = index > reachedIndex
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isCurrent}
              disabled={isLocked}
              onClick={() => setStepIndex(index)}
              className={cn(
                'flex flex-1 items-center justify-center gap-1 rounded-md py-2 text-xs font-bold uppercase tracking-widest transition-colors',
                isCurrent
                  ? 'bg-card text-foreground shadow-sm'
                  : isLocked
                    ? 'cursor-not-allowed text-muted-foreground/40'
                    : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {isConfirmed && !isCurrent && <Check className="h-3 w-3" strokeWidth={3} />}
              {label}
            </button>
          )
        })}
      </div>

      <div className="mt-5">
        {step === 'classes' && (
          <ClassesTab
            tableId={tableId || ''}
            classes={gameConfig?.classes || []}
            isGameMaster
            onClassesChange={reloadGameConfig}
            onSaveClass={handleSaveClass}
            onDeleteClass={handleDeleteClass}
          />
        )}
        {step === 'attributes' && (
          <AttributesTab
            tableId={tableId || ''}
            attributes={gameConfig?.attributes || []}
            isGameMaster
            onAttributesChange={reloadGameConfig}
            onSaveAttribute={handleSaveAttribute}
            onDeleteAttribute={handleDeleteAttribute}
          />
        )}
        {step === 'items' && (
          <ItemsTab
            tableId={tableId || ''}
            items={gameConfig?.items || []}
            isGameMaster
            onItemsChange={reloadGameConfig}
            onSaveItem={handleSaveItem}
            onDeleteItem={handleDeleteItem}
          />
        )}
      </div>

      {/* Botão fixo: confirma a etapa atual; só a última conclui o wizard */}
      <div className="fixed inset-x-0 bottom-0 z-10 bg-gradient-to-t from-background via-background/95 to-transparent px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4">
        <div className="mx-auto w-full max-w-3xl">
          {isLastStep ? (
            <Button
              type="button"
              size="lg"
              disabled={isFinishing}
              onClick={handleFinish}
              className="w-full font-black uppercase tracking-widest"
            >
              {isFinishing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" strokeWidth={3} />
              )}
              {isFinishing ? 'Concluindo…' : 'Concluir configuração'}
            </Button>
          ) : (
            <Button
              type="button"
              size="lg"
              onClick={handleConfirmStep}
              className="w-full font-black uppercase tracking-widest"
            >
              Confirmar {STEPS[stepIndex].label.toLowerCase()} e avançar
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={3} />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
